import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import ProjectService from "api/Project/ProjectService";
import { InferenceRequestLog } from "models/InferenceRequestLog";
import Tabs from "components/Tabs";
import NonUseTimingsDialog from "components/NontUseTimingsDialog";
import HelpHoverIcon from "components/HelpHoverIcon";
import RequestLogTable from "../RequestLogTable";
import LatencySlider from "../LatencySlider";
import { AppRoutes } from "pages/constants/routes";
import { DeploymentMode, getTransitionMode } from "models/DeploymentMode";
import { ProjectDeploymentInfo } from "models/ProjectDeploymentInfo";
import { mergeIntervals } from "utils/interval";

import styles from "./DeploymentTab.module.scss";

interface DeploymentTabProps {
  proj_id: string;
}

const defaultDeploymentInfo: ProjectDeploymentInfo = {
  current_mode: DeploymentMode.off,
  desired_mode: DeploymentMode.off,
  test: {
    projected_hourly_cost: 0,
  },
  production: {
    non_use_timings: [],
    latency_list: [],
    projected_hourly_cost_list: [],
    selected_index: 0,
  },
};

const DeploymentTab = ({ proj_id }: DeploymentTabProps) => {
  const [requestLogs, setRequestLogs] = useState<InferenceRequestLog[]>([]);
  const [deploymentInfo, setDeploymentInfo] = useState<ProjectDeploymentInfo>(
    defaultDeploymentInfo,
  );
  const isInTransition =
    deploymentInfo.current_mode !== deploymentInfo.desired_mode;

  const [chosenEditMode, setChosenEditMode] = useState<DeploymentMode>(
    DeploymentMode.off,
  );
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const displayMode: DeploymentMode = isEditing
    ? chosenEditMode
    : deploymentInfo.current_mode;

  const [openNonUseTimingsModel, setOpenNonUseTimingsModel] =
    useState<boolean>(false);
  const [latencyIndex, setLatencyIndex] = useState<number>(0);
  const latencyList = [100, 250, 300, 500];
  const costList = [0.52, 0.39, 0.29, 0.13];

  const getData = useCallback(async () => {
    try {
      const response = await ProjectService.getProjectDeploymentInfo(proj_id);

      setRequestLogs(response.logs);
      setDeploymentInfo(response.info);
    } catch (err) {
      // handle error
    }
  }, [proj_id]);

  useEffect(() => {
    getData();
    const interval = setInterval(getData, 30 * 1000); // 30s
    return () => clearInterval(interval);
  }, [getData]);

  const modeToTabIndex = (mode: DeploymentMode) => {
    switch (mode) {
      case DeploymentMode.off:
      case DeploymentMode.off_initialising:
        return 0;
      case DeploymentMode.test:
      case DeploymentMode.test_initialising:
        return 1;
      case DeploymentMode.production:
      case DeploymentMode.production_initialising:
        return 2;
    }
  };

  const tabIndexToMode = (tabIndex: number) =>
    tabIndex == 0
      ? DeploymentMode.off
      : tabIndex == 1
        ? DeploymentMode.test
        : DeploymentMode.production;

  const shouldShowEndpointInfo = (currentMode: DeploymentMode) => {
    if (isInTransition) {
      return false;
    }

    return (
      currentMode === DeploymentMode.test ||
      currentMode === DeploymentMode.production
    );
  };

  const onDeploymentModeTabChange = (tabIndex: number) => {
    const newMode = tabIndexToMode(tabIndex);
    setIsEditing(newMode != deploymentInfo.current_mode);
    setChosenEditMode(newMode);
  };

  const onConfirmDeploymentModeChange = () => {
    const newMode = chosenEditMode;
    ProjectService.changeDeploymentMode(
      proj_id,
      newMode,
      // mergeIntervals(
      //   deploymentInfo.production.non_use_timings.map((data) => [
      //     data.start,
      //     data.end,
      //   ]),
      // ),
      // deploymentInfo.production.selected_index,
    );

    setIsEditing(false);
    setChosenEditMode(newMode);
    setDeploymentInfo((current) => ({
      ...current,
      desired_mode: newMode,
      current_mode: getTransitionMode(newMode),
    }));
  };

  return (
    <div className={styles.content}>
      <div className={styles.header}>
        <div className={styles.title}>Deployment</div>
        <div className={styles.subtitle}>
          Manage your deployment mode and view request history
        </div>
      </div>

      <div
        className={`${styles.inferenceModeSection} ${isEditing && styles.editing}`}
      >
        {isInTransition && (
          <div className={styles.blurBlock}>
            {deploymentInfo.desired_mode !== DeploymentMode.off ? (
              <>
                <div>Please wait while we are setting up...</div>
                <div>
                  This should take at most 10 minutes, please{" "}
                  <Link to={AppRoutes.support}>contact us</Link> if this does
                  does not go away.
                </div>
              </>
            ) : (
              <span>Please wait while we tear down the instances...</span>
            )}
          </div>
        )}
        <div
          className={`${styles.modeControlSection} ${isEditing && styles.editing}`}
        >
          <div className={styles.title}>
            {isEditing ? "Change" : "Current"} Mode
          </div>
          <Tabs
            tabs={["Off", "Test", "Production"]}
            tabIndex={modeToTabIndex(displayMode)}
            setTabIndex={onDeploymentModeTabChange}
          />
        </div>

        {displayMode != DeploymentMode.off && (
          <div className={`${styles.content} ${isEditing && styles.editing}`}>
            <div
              className={`${styles.costProjectionTestBox} ${isEditing && styles.editing}`}
            >
              {displayMode === DeploymentMode.test && (
                <>
                  <div className={styles.sectionTitle}>Cost Projection</div>
                  <div className={styles.costTable}>
                    <div className={styles.left}>Hourly Rate</div>
                    <div className={styles.right}>SGD 0.065</div>
                    <div className={styles.left}>Monthly Rate</div>
                    <div className={styles.right}>SGD 46.8</div>
                  </div>
                </>
              )}
              {displayMode === DeploymentMode.production && (
                <>
                  <div className={styles.sectionTitle}>Cost Projection</div>
                  <div className={styles.costTable}>
                    <div className={styles.left}>Hourly Rate</div>
                    <div className={styles.right}>
                      SGD {costList[latencyIndex]}
                    </div>
                    <div className={styles.left}>Monthly Rate</div>
                    <div className={styles.right}>
                      SGD{" "}
                      {Math.round(costList[latencyIndex] * 24 * 30 * 100) / 100}
                    </div>
                  </div>
                </>
              )}
            </div>

            {displayMode == DeploymentMode.production && (
              <div className={styles.deploymentConfigSection}>
                <div className={styles.sectionTitle}>Configuration</div>
                <div className={styles.subsection}>
                  <span>Latency</span>
                  <LatencySlider
                    options={latencyList}
                    selectedIndex={latencyIndex}
                    setSelectedIndex={setLatencyIndex}
                  />
                </div>
                <div className={styles.subsection}>
                  <div className={styles.subsectionHeader}>
                    <span>Non-Use Timings</span>
                    <HelpHoverIcon message="Please select the period when no requests should be expected" />
                  </div>
                  <div
                    className={styles.nonUseTimingsButton}
                    onClick={() => setOpenNonUseTimingsModel(true)}
                  >
                    Select Timings
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {isEditing && (
          <div className={styles.controlsContainer}>
            <span className={styles.infoMessage}>
              This might take a few minutes
            </span>
            <div
              className={styles.saveButton}
              onClick={onConfirmDeploymentModeChange}
            >
              Confirm
            </div>
          </div>
        )}
      </div>

      {shouldShowEndpointInfo(deploymentInfo.current_mode) && (
        <div className={styles.endpointInfoSection}>
          <div className={styles.sectionTitle}>Endpoint</div>
          <div className={styles.endpointTable}>
            <div className={styles.key}>URL</div>
            <div>{process.env.REACT_APP_API_GATEWAY_URL ?? "-"}</div>
            <div className={styles.key}>Port</div>
            <div>{process.env.REACT_APP_API_GATEWAY_PORT ?? "8080"}</div>

            <div className={styles.rowSeparator} />

            <div className={styles.key}>Protocol</div>
            <div>{process.env.REACT_APP_API_GATEWAY_PROTOCOL ?? "HTTP"}</div>
          </div>
        </div>
      )}

      <div className={styles.logsSection}>
        <div className={styles.sectionTitle}>Logs</div>
        {requestLogs.length == 0 ? (
          <div className={styles.emptyRequests}>No Requests yet...</div>
        ) : (
          <div className="ag-theme-quartz" style={{ height: 300 }}>
            <RequestLogTable data={requestLogs} />
          </div>
        )}
      </div>

      <NonUseTimingsDialog
        open={openNonUseTimingsModel}
        setOpen={setOpenNonUseTimingsModel}
        nonUseTimings={deploymentInfo.production.non_use_timings}
        setNonUseTimings={(value) =>
          setDeploymentInfo((current) => ({
            ...current,
            production: { ...current.production, non_use_timings: value },
          }))
        }
      />
    </div>
  );
};

export default DeploymentTab;
